import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { RHFSwitchField } from '@openx/components-core/lib/SwitchField/RHFSwitchField.tsx';
import { dealFormField } from '@openx/types/deal.ts';
import { InfoIcon } from '@phosphor-icons/react/Info';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type PacingFormSwitchProps = {
  isSubmitting: boolean;
  label: string;
  isStartDateOneHourAhead: boolean;
  isPacingSupportedDeal: boolean;
  isPmpDealType: boolean;
};

export const PacingFormSwitch = ({
  label,
  isStartDateOneHourAhead,
  isPacingSupportedDeal,
  isSubmitting,
  isPmpDealType,
}: PacingFormSwitchProps) => {
  const { t } = useTranslation();

  const canUpdateSwitch = isPacingSupportedDeal && isStartDateOneHourAhead && !isPmpDealType;

  const tooltipTitle = useMemo(() => {
    if (!isPacingSupportedDeal) {
      return t('Pacing is available for Programmatic Guaranteed, Preferred Deal and Private Auction deal types only.');
    }

    if (isPmpDealType) {
      return t('It is not possible to switch off Pacing on an Programmatic Guaranteed deal.');
    }

    if (!isStartDateOneHourAhead) {
      return t('Pacing settings are locked if the Start Date is within the next hour.');
    }

    return '';
  }, [isStartDateOneHourAhead, isPacingSupportedDeal, isPmpDealType, t]);

  return (
    <Box sx={{ alignItems: 'center', display: 'flex' }}>
      <RHFSwitchField
        name={dealFormField.IS_PACING_ENABLED}
        label={label}
        data-test="pacing-switch"
        disabled={isSubmitting || !canUpdateSwitch}
      />
      {!!tooltipTitle && (
        <Tooltip title={tooltipTitle} placement="right">
          <IconButton size="small" data-test="pacing-tooltip-icon">
            <InfoIcon />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
